/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/knn.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Finds the k nearest vectors to a query vector, as measured by a similarity metric. knn function finds nearest vectors through approximate search on indexed dense_vectors or semantic_text fields.
 *
 * @example
 * from colors metadata _score
 * | where knn(rgb_vector, [0, 120, 0])
 * | sort _score desc, color asc
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'knn',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'dense_vector',
          optional: false,
        },
        {
          name: 'query',
          type: 'dense_vector',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'k\', values=[10], description=\'The number of nearest neighbors to return from each shard. Elasticsearch collects k results from each shard, then merges them to find the global top results. This value must be less than or equal to num_candidates. This value is automatically set with any LIMIT applied to the function.\', type=[integer]}, {name=\'boost\', values=[2.5], description=\'Floating point number used to decrease or increase the relevance scores of the query.Defaults to 1.0.\', type=[float]}, {name=\'min_candidates\', values=[10], description=\'The minimum number of nearest neighbor candidates to consider per shard while doing knn search.  KNN may use a higher number of candidates in case the query can\'t use a approximate results. Cannot exceed 10,000. Increasing min_candidates tends to improve the accuracy of the final results. Defaults to 1.5 * k (or LIMIT) used for the query.\', type=[integer]}, {name=\'visit_percentage\', values=[10], description=\'The percentage of vectors to explore per shard while doing knn search with bbq_disk. Must be between 0 and 100. 0 will default to using num_candidates for calculating the percent visited. Increasing visit_percentage tends to improve the accuracy of the final results. If visit_percentage is set for bbq_disk, num_candidates is ignored. Defaults to ~1% per shard for every 1 million vectors\', type=[float]}, {name=\'similarity\', values=[0.01], description=\'The minimum similarity required for a document to be considered a match. The similarity value calculated relates to the raw similarity used, not the document score.\', type=[double]}, {name=\'rescore_oversample\', values=[3.5], description=\'Applies the specified oversampling for rescoring quantized vectors. See [oversampling and rescoring quantized vectors](docs-content://solutions/search/vector/knn.md#dense-vector-knn-search-rescoring) for details.\', type=[double]}',
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'query',
          type: 'dense_vector',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'k\', values=[10], description=\'The number of nearest neighbors to return from each shard. Elasticsearch collects k results from each shard, then merges them to find the global top results. This value must be less than or equal to num_candidates. This value is automatically set with any LIMIT applied to the function.\', type=[integer]}, {name=\'boost\', values=[2.5], description=\'Floating point number used to decrease or increase the relevance scores of the query.Defaults to 1.0.\', type=[float]}, {name=\'min_candidates\', values=[10], description=\'The minimum number of nearest neighbor candidates to consider per shard while doing knn search.  KNN may use a higher number of candidates in case the query can\'t use a approximate results. Cannot exceed 10,000. Increasing min_candidates tends to improve the accuracy of the final results. Defaults to 1.5 * k (or LIMIT) used for the query.\', type=[integer]}, {name=\'visit_percentage\', values=[10], description=\'The percentage of vectors to explore per shard while doing knn search with bbq_disk. Must be between 0 and 100. 0 will default to using num_candidates for calculating the percent visited. Increasing visit_percentage tends to improve the accuracy of the final results. If visit_percentage is set for bbq_disk, num_candidates is ignored. Defaults to ~1% per shard for every 1 million vectors\', type=[float]}, {name=\'similarity\', values=[0.01], description=\'The minimum similarity required for a document to be considered a match. The similarity value calculated relates to the raw similarity used, not the document score.\', type=[double]}, {name=\'rescore_oversample\', values=[3.5], description=\'Applies the specified oversampling for rescoring quantized vectors. See [oversampling and rescoring quantized vectors](docs-content://solutions/search/vector/knn.md#dense-vector-knn-search-rescoring) for details.\', type=[double]}',
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
